/**
 * WHY ONE `NotFoundError` IS THREE DIFFERENT FACTS WITH THREE DIFFERENT FIXES.
 *
 * `navigator.usb.requestDevice()` rejects with exactly one string —
 *
 *     NotFoundError: Failed to execute 'requestDevice' on 'USB': No device selected.
 *
 * — in every one of these situations, which are not the same problem:
 *
 *   1. The chooser opened and the operator pressed Cancel.       → just try again
 *   2. The chooser opened EMPTY: nothing on the USB bus matched. → it's power or the cable
 *   3. The chooser NEVER OPENED, because this browser has no USB → open the till in Chrome
 *      picker UI at all (embedded browsers / webviews).
 *
 * Case 3 is the one that wastes an operator's shift, because the screen shows nothing at all
 * and the message blames a selection they were never offered the chance to make.
 *
 * HOW WE TELL 3 APART FROM 1 AND 2 — a measurement, not a guess.
 *
 * Driving the till's Connect button in the embedded browser, with an EMPTY filter list
 * (`filters: []` — "offer me every device you are willing to show"), the promise rejected in
 * **3 ms**. A human cannot read and dismiss a dialog in 3 ms, so a rejection that fast is
 * proof that no dialog was ever drawn. Cancelling a real chooser, or opening it, seeing an
 * empty list and closing it, is a human action and costs hundreds of milliseconds at minimum.
 *
 * So the elapsed time separates them deterministically — no user-agent sniffing, and no
 * reliance on a capability check that is already wrong: `'usb' in navigator` is `true` in the
 * embedded browser, which is precisely why the operator is offered a button that cannot work.
 *
 * CASES 1 AND 2 STAY MERGED, deliberately. From here they are genuinely indistinguishable —
 * both are "a chooser was shown and no device came back". The operator does not need us to
 * guess: they know whether they clicked Cancel. The message names the other cause so that if
 * they didn't, the next move is already on screen.
 */

/**
 * Below this, no human was involved, so no chooser was shown.
 *
 * Measured at 3 ms; 250 ms leaves two orders of magnitude of headroom for a slow machine while
 * staying far under the fastest plausible human dismissal of a dialog.
 */
export const NO_CHOOSER_MS = 250;

export function describeConnectFailure(error: unknown, elapsedMs: number): string {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const name = (error as { name?: string } | null)?.name;

  if (name === "NotFoundError" || /No device selected/i.test(message)) {
    return elapsedMs < NO_CHOOSER_MS
      ? "This browser can't show the USB printer chooser. Open the till in Google Chrome to connect the printer."
      : "No printer was picked. If the list was empty the printer isn't reachable — check it's switched on and plugged straight into this computer with a data cable, not through a hub or adapter.";
  }

  // A secure context is required for WebUSB. `http://localhost` counts; a plain-http LAN
  // address does not, and that is a realistic way to run a till on a venue network.
  if (/secure context/i.test(message)) {
    return "The printer can only be connected over HTTPS, or on localhost. Open the till on its https address.";
  }

  // The OS driver already owns the device — on macOS a CUPS queue for the same printer is the
  // usual culprit, since WebUSB needs to claim the interface exclusively.
  if (name === "SecurityError" || /Access denied/i.test(message)) {
    return "Another program is holding the printer. Close the other app or tab using it, then try again.";
  }

  return message || "Could not connect to the printer.";
}

/**
 * Runs a connect attempt and returns an operator-readable failure, or `null` on success.
 *
 * The timing lives here rather than at each call site so that every Connect button gets the
 * case-3 diagnosis for free — a rule you have to remember to apply is one that gets forgotten.
 */
export async function connectWithDiagnosis(connect: () => Promise<unknown>): Promise<string | null> {
  const startedAt = performance.now();
  try {
    await connect();
    return null;
  } catch (error) {
    return describeConnectFailure(error, performance.now() - startedAt);
  }
}
